import { useEffect } from 'react'

interface SEOConfig {
  title: string
  description: string
  url: string
  image?: string
  type?: string
  locale?: string
}

export function useSEO(config: SEOConfig) {
  useEffect(() => {
    // Set lang attribute
    document.documentElement.lang = config.locale?.split('_')[0] || 'es'

    // Title
    document.title = config.title

    // Helper to set or create meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(
        `meta[${attr}="${key}"]`,
      ) as HTMLMetaElement | null
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.content = content
    }

    // Basic SEO
    setMeta('name', 'description', config.description)
    setMeta('name', 'robots', 'index, follow')
    setMeta('name', 'author', 'Mega Pack Gestión Organizacional')

    // Open Graph
    setMeta('property', 'og:title', config.title)
    setMeta('property', 'og:description', config.description)
    setMeta('property', 'og:type', config.type || 'website')
    setMeta('property', 'og:url', config.url)
    setMeta('property', 'og:locale', config.locale || 'es_AR')
    setMeta('property', 'og:site_name', 'Mega Pack Gestión Organizacional')
    if (config.image) {
      setMeta('property', 'og:image', config.image)
      setMeta('property', 'og:image:width', '1200')
      setMeta('property', 'og:image:height', '630')
    }

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', config.title)
    setMeta('name', 'twitter:description', config.description)
    if (config.image) {
      setMeta('name', 'twitter:image', config.image)
    }

    // Canonical URL
    let canonical = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = config.url

    // Structured Data (JSON-LD) - FAQPage for SEO
    let jsonLd = document.querySelector(
      'script[data-seo="structured-data"]',
    ) as HTMLScriptElement | null
    if (!jsonLd) {
      jsonLd = document.createElement('script')
      jsonLd.type = 'application/ld+json'
      jsonLd.setAttribute('data-seo', 'structured-data')
      document.head.appendChild(jsonLd)
    }
    jsonLd.textContent = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Mega Pack Gestión Organizacional',
        description: config.description,
        url: config.url,
        image: config.image,
        brand: {
          '@type': 'Brand',
          name: 'Mega Pack Gestión Organizacional',
        },
        offers: [
          {
            '@type': 'Offer',
            name: 'Pack Base',
            price: '19990',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            url: 'https://fegion.shop/cart/46195221364875:1',
          },
          {
            '@type': 'Offer',
            name: 'Pack Implementación',
            price: '29990',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            url: 'https://fegion.shop/cart/46195232702603:1',
          },
          {
            '@type': 'Offer',
            name: 'Pack Control Total',
            price: '39990',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            url: 'https://fegion.shop/cart/46195236503691:1',
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿En qué formato recibiré los materiales?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Todo el contenido es 100% digital en formato PDF de alta calidad. Las plantillas editables del Nivel 2 y 3 vienen en formato Word compatible.',
            },
          },
          {
            '@type': 'Question',
            name: '¿Necesito conocimientos previos de management?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. El contenido está diseñado para ser accesible desde cero y progresivo.',
            },
          },
          {
            '@type': 'Question',
            name: '¿El contenido aplica a cualquier tipo de empresa?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'El sistema está diseñado para PyMEs y empresas medianas de cualquier sector.',
            },
          },
        ],
      },
    ])

    // Preconnect ONLY to origins we actually use
    const preconnectOrigin = 'https://cdn.magicpatterns.com'
    if (
      !document.querySelector(
        `link[rel="preconnect"][href="${preconnectOrigin}"]`,
      )
    ) {
      const link = document.createElement('link')
      link.rel = 'preconnect'
      link.href = preconnectOrigin
      link.crossOrigin = 'anonymous'
      document.head.appendChild(link)
    }

    // Remove unused Google Fonts preconnects if they exist (they hurt performance)
    document
      .querySelectorAll(
        'link[rel="preconnect"][href*="fonts.googleapis"], link[rel="preconnect"][href*="fonts.gstatic"]',
      )
      .forEach((el) => el.remove())

    // Preload LCP image
    const lcpImage =
      'https://cdn.magicpatterns.com/uploads/5q59V8L4m6ADUSNGRFBC9d/ChatGPT_Image_10_mar_2026,_00_45_48.png'
    if (!document.querySelector(`link[rel="preload"][href="${lcpImage}"]`)) {
      const preload = document.createElement('link')
      preload.rel = 'preload'
      preload.as = 'image'
      preload.href = lcpImage
      ;(preload as any).fetchPriority = 'high'
      document.head.appendChild(preload)
    }
  }, [
    config.title,
    config.description,
    config.url,
    config.image,
    config.type,
    config.locale,
  ])
}
